import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelAppTabAction, ChannelAppTabUpdateInput } from '../../../services';
import { LOCALES } from '../../constants';
import { LanguageByCode } from '../../utils';
import { InputField } from '../InputField';
import { TabItem, Tabs } from '../Tabs';

import classes from './AppTabsSettings.module.scss';

interface TabLocaleChangeArgs {
  type: ChannelAppTabAction;
  language: string;
  localization: string;
}

interface TabIconChangeArgs {
  type: ChannelAppTabAction;
  platform: string;
  value: string;
}

interface AppTabsSettingsProps {
  appTabs: ChannelAppTabUpdateInput[];
  setAppTabs: Dispatch<SetStateAction<ChannelAppTabUpdateInput[]>>;
  langHintLocaliseKey: string;
  iconHintLocaliseKey: string;
}

export const AppTabsSettings: FunctionComponent<AppTabsSettingsProps> = memo(
  ({ appTabs, setAppTabs, langHintLocaliseKey, iconHintLocaliseKey }) => {
    const { t } = useTranslation();

    const [selectedIconTabIndex, setSelectedIconTabIndex] = useState(0);

    const [selectedLocaleTabIndex, setSelectedLocaleTabIndex] = useState(0);

    const timeStamp = useMemo(() => Date.now(), []);

    const onTabLocaleChange = useCallback(
      ({ type, language, localization }: TabLocaleChangeArgs) => {
        setAppTabs((appTabsPrev) =>
          appTabsPrev.map((appTab) =>
            appTab.action.type === type
              ? {
                  ...appTab,
                  title: appTab.title.map((t) =>
                    t.language === language ? { ...t, localization } : t
                  ),
                }
              : appTab
          )
        );
      },
      [setAppTabs]
    );

    const onTabIconChange = useCallback(
      ({ type, platform, value }: TabIconChangeArgs) => {
        setAppTabs((appTabsPrev) =>
          appTabsPrev.map((appTab) =>
            appTab.action.type === type
              ? { ...appTab, iconUrl: { ...appTab.iconUrl, [platform]: value } }
              : appTab
          )
        );
      },
      [setAppTabs]
    );

    const iconTabItems: TabItem[] = useMemo(
      () =>
        ['x1', 'x3'].map((platform, index) => {
          const iconTabName = t(`appTabsSettings.icon-${platform}`);

          return {
            index,
            name: iconTabName,
            content: (
              <div className={classes['tab-content']}>
                {appTabs.map(({ action: { type }, iconUrl }) => {
                  const typeLabel = t(`appTabs.${type}`);

                  return (
                    <div key={type} className={classes['tab-content__item']}>
                      <div className={classes['tab-content__item-type-wrapper']}>
                        {iconUrl.x3 && (
                          <div
                            style={{
                              maskImage: `url('${iconUrl.x3}?date=${timeStamp}')`,
                              WebkitMaskImage: `url('${iconUrl.x3}?date=${timeStamp}')`,
                              maskRepeat: 'no-repeat',
                              WebkitMaskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskPosition: 'center',
                              maskSize: 'cover',
                              WebkitMaskSize: 'cover',
                              width: '1.5rem',
                              height: '1.5rem',
                              backgroundColor: '#000',
                            }}
                          />
                        )}
                        <div className={classes['tab-content__item-type']}>{typeLabel}</div>
                      </div>
                      <InputField
                        className={classes['tab-content__item-input']}
                        value={iconUrl[platform as 'x1' | 'x3'] ?? ''}
                        ariaLabel={`${typeLabel}, ${iconTabName}`}
                        onChange={({ target }) =>
                          onTabIconChange({ type, platform, value: target.value })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ),
          };
        }),
      [appTabs, onTabIconChange, t, timeStamp]
    );

    const localeTabItems: TabItem[] = useMemo(
      () =>
        LOCALES.map(({ code }, index) => {
          return {
            index,
            name: LanguageByCode[code as keyof typeof LanguageByCode],
            content: (
              <div className={classes['tab-content']}>
                {appTabs.map(({ action: { type }, iconUrl: { x3 }, title }) => {
                  const typeLabel = t(`appTabs.${type}`);

                  return (
                    <div key={type} className={classes['tab-content__item']}>
                      <div className={classes['tab-content__item-type-wrapper']}>
                        {x3 && (
                          <div
                            style={{
                              maskImage: `url('${x3}?date=${timeStamp}')`,
                              WebkitMaskImage: `url('${x3}?date=${timeStamp}')`,
                              maskRepeat: 'no-repeat',
                              WebkitMaskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskPosition: 'center',
                              maskSize: 'cover',
                              WebkitMaskSize: 'cover',
                              width: '1.5rem',
                              height: '1.5rem',
                              backgroundColor: '#000',
                            }}
                          />
                        )}
                        <div className={classes['tab-content__item-type']}>{typeLabel}</div>
                      </div>
                      <InputField
                        className={classes['tab-content__item-input']}
                        value={title.find(({ language }) => language === code)?.localization ?? ''}
                        ariaLabel={typeLabel}
                        onChange={({ target }) =>
                          onTabLocaleChange({ type, language: code, localization: target.value })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ),
          };
        }),
      [appTabs, onTabLocaleChange, t, timeStamp]
    );

    return (
      <>
        <div className={classes['tab-wrapper']}>
          <div className={classes['tab-wrapper__hint']}>{t(langHintLocaliseKey)}</div>
          <Tabs
            items={localeTabItems}
            selectedTabIndex={selectedLocaleTabIndex}
            setSelectedTabIndex={setSelectedLocaleTabIndex}
          />
        </div>

        <div className={classes['tab-wrapper']}>
          <div className={classes['tab-wrapper__hint']}>{t(iconHintLocaliseKey)}</div>
          <Tabs
            items={iconTabItems}
            selectedTabIndex={selectedIconTabIndex}
            setSelectedTabIndex={setSelectedIconTabIndex}
          />
        </div>
      </>
    );
  }
);
